import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Spinner } from 'react-bootstrap';
import DashboardLayout from '../components/DashboardLayout';
import { API_BASE_URL, apiRequest, clearAuthSession } from '../utils/api';
import '../assets/styles/reports.css';
import { getActiveCompanyId } from '../utils/companyContext';
import { hasModulePermission } from '../utils/permissions';

const buildAuthHeaders = () => {
  const token = localStorage.getItem('authToken');
  const companyId = getActiveCompanyId();

  return {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(companyId ? { 'x-company-id': companyId } : {}),
  };
};

const EmployeeExport = () => {
  const navigate = useNavigate();
  const [fieldOptions, setFieldOptions] = useState([]);
  const [selectedFields, setSelectedFields] = useState([]);
  const [outletOptions, setOutletOptions] = useState([]);
  const [designationOptions, setDesignationOptions] = useState([]);
  const [activeCompanyId, setActiveCompanyId] = useState(() => getActiveCompanyId());
  const [filters, setFilters] = useState({
    outletId: '',
    designation: '',
    employeeId: null,
    employeeStatus: 'active',
  });
  const [optionsError, setOptionsError] = useState(null);
  const [loadingOptions, setLoadingOptions] = useState(false);

  const [employeeQuery, setEmployeeQuery] = useState('');
  const [employeeResults, setEmployeeResults] = useState([]);
  const [employeeLoading, setEmployeeLoading] = useState(false);
  const [employeeError, setEmployeeError] = useState(null);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const canExportEmployees = hasModulePermission('reports.employee_export', 'export');

  const [exportError, setExportError] = useState(null);
  const [exportSuccess, setExportSuccess] = useState(null);
  const [isExporting, setIsExporting] = useState(false);

  useEffect(() => {
    const handleActiveCompanyChanged = (event) => {
      const nextCompanyId = event?.detail?.id ?? getActiveCompanyId();
      setActiveCompanyId(nextCompanyId);
      setFilters((prev) => ({ ...prev, outletId: '', designation: '', employeeId: null }));
      setSelectedEmployee(null);
      setEmployeeQuery('');
      setEmployeeResults([]);
    };

    window.addEventListener('activeCompanyChanged', handleActiveCompanyChanged);
    return () => {
      window.removeEventListener('activeCompanyChanged', handleActiveCompanyChanged);
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    const loadOptions = async () => {
      setLoadingOptions(true);
      setOptionsError(null);
      try {
        if (!activeCompanyId) {
          setOutletOptions([]);
          setDesignationOptions([]);
          setFieldOptions([]);
          setSelectedFields([]);
          return;
        }
        const params = new URLSearchParams({ companyId: String(activeCompanyId) });
        const { data } = await apiRequest(`/api/admin/reports/employees/options?${params.toString()}`, {}, navigate);
        if (!isMounted) {
          return;
        }

        const fields = Array.isArray(data?.fields) ? data.fields : [];
        setFieldOptions(fields);
        setSelectedFields(fields.map((field) => field.key));
        setOutletOptions(Array.isArray(data?.outlets) ? data.outlets : []);
        setDesignationOptions(Array.isArray(data?.designations) ? data.designations : []);
      } catch (error) {
        if (isMounted) {
          setOptionsError(error.message || 'Failed to load export options');
        }
      } finally {
        if (isMounted) {
          setLoadingOptions(false);
        }
      }
    };

    loadOptions();

    return () => {
      isMounted = false;
    };
  }, [activeCompanyId, navigate]);

  useEffect(() => {
    if (!employeeQuery || selectedEmployee) {
      setEmployeeResults([]);
      setEmployeeError(null);
      return undefined;
    }

    let isActive = true;
    const controller = new AbortController();
    const timer = setTimeout(async () => {
      setEmployeeLoading(true);
      setEmployeeError(null);
      try {
        const response = await fetch(
          `${API_BASE_URL}/api/admin/reports/employees/search?q=${encodeURIComponent(
            employeeQuery.trim(),
          )}&limit=10&employeeStatus=${encodeURIComponent(filters.employeeStatus)}${activeCompanyId ? `&companyId=${encodeURIComponent(activeCompanyId)}` : ''}`,
          {
            method: 'GET',
            headers: buildAuthHeaders(),
            signal: controller.signal,
          },
        );

        if (response.status === 401) {
          clearAuthSession(navigate);
          return;
        }

        const result = await response.json();
        if (!response.ok || result?.status !== 'SUCCESS') {
          throw new Error(result?.message || 'Failed to search employees');
        }

        if (isActive) {
          setEmployeeResults(Array.isArray(result?.data) ? result.data : []);
        }
      } catch (error) {
        if (error.name === 'AbortError') {
          return;
        }
        if (isActive) {
          setEmployeeError(error.message || 'Unable to search employees');
          setEmployeeResults([]);
        }
      } finally {
        if (isActive) {
          setEmployeeLoading(false);
        }
      }
    }, 300);

    return () => {
      isActive = false;
      controller.abort();
      clearTimeout(timer);
    };
  }, [activeCompanyId, employeeQuery, filters.employeeStatus, navigate, selectedEmployee]);

  const selectedFieldSet = useMemo(() => new Set(selectedFields), [selectedFields]);
  const allSelected =
    fieldOptions.length > 0 && fieldOptions.every((field) => selectedFieldSet.has(field.key));

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedFields(fieldOptions.filter((field) => field.isMandatory).map((field) => field.key));
    } else {
      setSelectedFields(fieldOptions.map((field) => field.key));
    }
  };

  const toggleField = (key) => {
    setSelectedFields((prev) => {
      const targetField = fieldOptions.find((field) => field.key === key);
      if (targetField?.isMandatory) {
        return prev;
      }

      const updated = new Set(prev);
      if (updated.has(key)) {
        updated.delete(key);
      } else {
        updated.add(key);
      }

      return fieldOptions.map((field) => field.key).filter((fieldKey) => updated.has(fieldKey));
    });
  };

  const handleFilterChange = (key, value) => {
    if (key === 'employeeStatus' && value !== filters.employeeStatus) {
      setSelectedEmployee(null);
      setEmployeeQuery('');
      setEmployeeResults([]);
      setEmployeeError(null);
    }
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleSelectEmployee = (employee) => {
    if (!employee) {
      return;
    }
    setSelectedEmployee(employee);
    setEmployeeQuery('');
    setEmployeeResults([]);
    setEmployeeError(null);
    setFilters((prev) => ({ ...prev, employeeId: employee.id }));
  };

  const clearSelectedEmployee = () => {
    setSelectedEmployee(null);
    setFilters((prev) => ({ ...prev, employeeId: null }));
  };

  const handleExport = async () => {
    if (!canExportEmployees) {
      setExportError('You do not have permission to export employee details.');
      return;
    }

    if (selectedFields.length === 0) {
      setExportError('Please select at least one column before exporting.');
      return;
    }

    const companyId = activeCompanyId;

    setExportError(null);
    setExportSuccess(null);
    setIsExporting(true);

    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/reports/employees/export`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...buildAuthHeaders(),
        },
        body: JSON.stringify({
          fields: selectedFields,
          filters: {
            outletId: filters.outletId || null,
            designation: filters.designation || null,
            employeeId: filters.employeeId || null,
            employeeStatus: filters.employeeStatus || 'active',
            companyId: companyId || null,
          },
        }),
      });

      if (response.status === 401) {
        clearAuthSession(navigate);
        return;
      }

      if (!response.ok) {
        let message = 'Failed to export employees.';
        try {
          const result = await response.json();
          if (result?.message) {
            message = result.message;
          }
        } catch (error) {
          // ignore json parse error
        }
        throw new Error(message);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `employee-export-${new Date().toISOString().slice(0, 10)}.xls`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setExportSuccess('Export started. Your file should begin downloading shortly.');
    } catch (error) {
      setExportError(error.message || 'Failed to export employees.');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <DashboardLayout title="Export Employee Details">
      <div className="report-action-bar mb-4">
        <div>
          <h2 className="fw-semibold mb-1">Export Employee Details</h2>
          <p className="text-muted mb-0">Choose the fields and filters before exporting the XLS file.</p>
        </div>
        <div className="d-flex flex-wrap gap-2">
          <button type="button" className="btn btn-outline-secondary" onClick={() => navigate('/reports')}>
            Back to Reports
          </button>
          <button
            type="button"
            className="btn btn-danger"
            onClick={handleExport}
            disabled={!canExportEmployees || isExporting || selectedFields.length === 0}
          >
            {isExporting ? 'Exporting…' : 'Export'}
          </button>
        </div>
      </div>

      {optionsError && (
        <div className="alert alert-danger" role="alert">
          {optionsError}
        </div>
      )}

      {exportError && (
        <div className="alert alert-danger report-status-message" role="alert">
          {exportError}
        </div>
      )}

      {exportSuccess && (
        <div className="alert alert-success report-status-message" role="alert">
          {exportSuccess}
        </div>
      )}

      <div className="employee-export-card">
        {loadingOptions ? (
          <div className="text-center py-5">
            <Spinner animation="border" role="status" />
          </div>
        ) : (
          <>
            <div className="report-select-all">
              <input
                type="checkbox"
                id="report-select-all"
                checked={allSelected}
                onChange={toggleSelectAll}
              />
              <label htmlFor="report-select-all" className="mb-0">
                Select all
              </label>
            </div>

            <div className="report-field-grid mb-4">
              {fieldOptions.map((field) => (
                <label key={field.key} className="report-field-checkbox">
                  <input
                    type="checkbox"
                    checked={selectedFieldSet.has(field.key)}
                    onChange={() => toggleField(field.key)}
                    disabled={field.isMandatory}
                  />
                  <span>{field.label}</span>
                </label>
              ))}
            </div>

            <p className="report-info-text mb-4">
              Tip: Use the filters below to narrow down the employees before exporting.
            </p>

            <div className="report-filter-grid">
              <div className="report-filter">
                <label htmlFor="report-filter-outlet">Outlet</label>
                <select
                  id="report-filter-outlet"
                  value={filters.outletId}
                  onChange={(event) => handleFilterChange('outletId', event.target.value)}
                >
                  <option value="">All outlets</option>
                  {outletOptions.map((outlet) => (
                    <option key={outlet.id} value={outlet.id}>
                      {outlet.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="report-filter">
                <label htmlFor="report-filter-designation">Designation</label>
                <select
                  id="report-filter-designation"
                  value={filters.designation}
                  onChange={(event) => handleFilterChange('designation', event.target.value)}
                >
                  <option value="">All designations</option>
                  {designationOptions.map((designation) => (
                    <option key={designation.value} value={designation.value}>
                      {designation.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="report-filter">
                <label htmlFor="report-filter-employee-status">Employee Status</label>
                <select
                  id="report-filter-employee-status"
                  value={filters.employeeStatus}
                  onChange={(event) => handleFilterChange('employeeStatus', event.target.value)}
                >
                  <option value="active">Active Employees</option>
                  <option value="inactive">Inactive Employees</option>
                  <option value="all">All Employees</option>
                </select>
              </div>

              <div className="report-filter">
                <label htmlFor="report-filter-employee">Employee</label>
                {selectedEmployee ? (
                  <div className="report-selected-employee">
                    <span>
                      {selectedEmployee.name}
                      {selectedEmployee.showEmployeeId
                        ? ` (${selectedEmployee.showEmployeeId})`
                        : ''}
                    </span>
                    <button type="button" onClick={clearSelectedEmployee} aria-label="Clear employee">
                      ×
                    </button>
                  </div>
                ) : (
                  <div className="report-employee-search">
                    <input
                      id="report-filter-employee"
                      type="text"
                      value={employeeQuery}
                      placeholder="Search by name, ID or phone"
                      onChange={(event) => setEmployeeQuery(event.target.value)}
                      autoComplete="off"
                    />
                    {employeeQuery && (
                      <div className="report-search-dropdown">
                        {employeeLoading ? (
                          <div className="report-search-item text-muted">Searching…</div>
                        ) : employeeError ? (
                          <div className="report-search-item text-danger">{employeeError}</div>
                        ) : employeeResults.length === 0 ? (
                          <div className="report-search-item text-muted">No employees found</div>
                        ) : (
                          employeeResults.map((employee) => (
                            <button
                              type="button"
                              key={employee.id}
                              className="report-search-item text-start"
                              onClick={() => handleSelectEmployee(employee)}
                            >
                              <strong>{employee.name}</strong>
                              <span>
                                {[employee.showEmployeeId, employee.phone]
                                  .filter(Boolean)
                                  .join(' · ')}
                              </span>
                            </button>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </DashboardLayout>
  );
};

export default EmployeeExport;
